import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Typography, Paper, Container,
  Select, MenuItem, FormControl, InputLabel, Box, colors
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import PaletteIcon from '@mui/icons-material/Palette';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import BrushIcon from '@mui/icons-material/Brush';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import ImageIcon from '@mui/icons-material/Image';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ClearFormatIcon from '@mui/icons-material/FormatClear';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';

// DrawingCanvas component for simple doodling
const DrawingCanvas = ({ onSave, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#424242'); // Dark gray for professional look
  const [brushSize, setBrushSize] = useState(5);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Set canvas dimensions
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = nativeEvent;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(offsetX, offsetY);
    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const ctx = canvasRef.current.getContext('2d');
    ctx.closePath();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveDrawing = () => {
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png');
    onSave(dataURL);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300, // Higher than AppBar
        p: 2,
      }}
    >
      <Paper elevation={10} sx={{ p: 4, width: '100%', maxWidth: '600px', borderRadius: '12px' }}>
        <Typography variant="h5" component="h3" sx={{ mb: 3, textAlign: 'center', color: colors.deepPurple[700], fontWeight: 'bold' }}>
          Doodle Pad!
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3, p: 2, backgroundColor: colors.deepPurple[50], borderRadius: '8px' }}>
          <Typography variant="body2" sx={{ color: colors.grey[700] }}>Color:</Typography>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: '32px', height: '32px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer' }} />
          <Typography variant="body2" sx={{ color: colors.grey[700], ml: 1 }}>Size:</Typography>
          <input type="range" min="1" max="20" value={brushSize} onChange={(e) => setBrushSize(parseInt(e.target.value))} style={{ width: '120px', accentColor: colors.deepPurple[500] }} />
          <Button variant="outlined" startIcon={<ClearFormatIcon />} onClick={clearCanvas} sx={{ ml: 2, color: colors.amber[700], borderColor: colors.amber[700] }}>Clear</Button>
        </Box>
        <Box sx={{ border: `2px dashed ${colors.deepPurple[300]}`, borderRadius: '8px', overflow: 'hidden', mb: 3 }}>
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={(e) => { e.preventDefault(); startDrawing({ nativeEvent: e.touches[0] }); }}
            onTouchMove={(e) => { e.preventDefault(); draw({ nativeEvent: e.touches[0] }); }}
            onTouchEnd={stopDrawing}
            style={{ width: '100%', height: '320px', backgroundColor: colors.grey[50], cursor: 'crosshair' }}
          ></canvas>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={saveDrawing}>
            Insert Doodle
          </Button>
          <Button variant="outlined" color="error" startIcon={<CloseIcon />} onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

// Main App component for the writing space
const WritingSpace = () => {
  // State to hold the content of the editor
  const [content, setContent] = useState('');
  // Ref to the contenteditable div
  const editorRef = useRef(null);
  // State for word count
  const [wordCount, setWordCount] = useState(0);
  // State for character count
  const [charCount, setCharCount] = useState(0);
  // State for showing a save/load message
  const [message, setMessage] = useState('');
  // State to control drawing canvas modal visibility
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  // State to control emoji picker visibility
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Common emojis for the picker
  const emojis = ['😊', '😂', '👍', '❤️', '🌟', '�', '📚', '✏️', '💡', '🚀', '🥳', '🌸', '✨', '🎶', '🎨'];

  // Effect to load content from localStorage on initial render
  useEffect(() => {
    const savedContent = localStorage.getItem('studentWritingContent');
    if (savedContent) {
      setContent(savedContent);
      if (editorRef.current) {
        editorRef.current.innerHTML = savedContent;
      }
    }
  }, []);

  // Function to update content state and counts whenever the editor content changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML;
      setContent(currentContent);

      // Calculate word count
      const textContent = editorRef.current.textContent || editorRef.current.innerText;
      const words = textContent.trim().split(/\s+/).filter(word => word.length > 0);
      setWordCount(words.length);

      // Calculate character count
      setCharCount(textContent.length);
    }
  }, []);

  // Effect to update counts when content changes (useful for initial load or programmatic changes)
  useEffect(() => {
    handleInput();
  }, [content, handleInput]);

  // Function to apply formatting commands to the selected text
  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    // Re-focus the editor after applying format
    editorRef.current.focus();
    handleInput(); // Update content and counts after formatting
  };

  // Function to save the current content to localStorage
  const saveContent = () => {
    localStorage.setItem('studentWritingContent', content);
    setMessage('Content saved successfully!');
    setTimeout(() => setMessage(''), 3000); // Clear message after 3 seconds
  };

  // Function to load content from localStorage
  const loadContent = () => {
    const savedContent = localStorage.getItem('studentWritingContent');
    if (savedContent) {
      setContent(savedContent);
      if (editorRef.current) {
        editorRef.current.innerHTML = savedContent;
      }
      setMessage('Content loaded successfully!');
    } else {
      setMessage('No saved content found.');
    }
    setTimeout(() => setMessage(''), 3000);
  };

  // Function to clear the editor content
  const clearContent = () => {
    setContent('');
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    setMessage('Content cleared.');
    setTimeout(() => setMessage(''), 3000);
  };

  // Function to insert HTML content at the current cursor position
  const insertHtmlAtCursor = (html) => {
    if (editorRef.current) {
      editorRef.current.focus(); // Ensure editor is focused
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents(); // Remove selected content

        // Create a temporary div to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Create a document fragment to insert nodes efficiently
        const fragment = document.createDocumentFragment();
        let lastNode = null;
        while (tempDiv.firstChild) {
          lastNode = fragment.appendChild(tempDiv.firstChild);
        }

        range.insertNode(fragment);

        // Position the cursor after the inserted content
        if (lastNode) {
          // If the last inserted node is an element, place cursor after it
          if (lastNode.nodeType === Node.ELEMENT_NODE) {
            range.setStartAfter(lastNode);
          } else {
            // If it's a text node, place cursor at the end of it
            range.setStart(lastNode, lastNode.length);
          }
          range.collapse(true); // Collapse the range to a cursor
          selection.removeAllRanges(); // Clear existing ranges
          selection.addRange(range); // Add the new range
        }
      } else {
        // If no selection, append to the end of the editor content
        editorRef.current.innerHTML += html;
        // Move cursor to the end after appending
        const newRange = document.createRange();
        const newSelection = window.getSelection();
        newRange.selectNodeContents(editorRef.current);
        newRange.collapse(false); // Collapse to the end
        newSelection.removeAllRanges();
        newSelection.addRange(newRange);
      }
      handleInput(); // Update content state and counts
    }
  };

  // Handle saving drawing from the canvas
  const handleSaveDrawing = (dataURL) => {
    insertHtmlAtCursor(`<img src="${dataURL}" alt="Doodle" style="max-width: 100%; height: auto; margin: 8px 0; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />`);
    setShowDrawingCanvas(false);
  };

  // Handle inserting emoji
  const handleInsertEmoji = (emoji) => {
    insertHtmlAtCursor(emoji);
    setShowEmojiPicker(false);
  };

  // Insert a placeholder image
  const insertPlaceholderImage = () => {
    const imageUrl = `https://placehold.co/300x200/${colors.blueGrey[100].replace('#', '')}/${colors.blueGrey[900].replace('#', '')}?text=Your+Image`;
    insertHtmlAtCursor(`<img src="${imageUrl}" alt="Placeholder" style="max-width: 100%; height: auto; margin: 8px 0; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />`);
  };

  // Handle Enter key to ensure new paragraphs are created with <p> tags
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default behavior (e.g., inserting <br> or <div>)
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const newParagraph = document.createElement('p');
        newParagraph.innerHTML = '&nbsp;'; // Use non-breaking space for an empty paragraph
        range.insertNode(newParagraph);
        range.setStart(newParagraph, 0); // Place cursor at the start of the new paragraph
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, fontFamily: 'Roboto, sans-serif' }}>
      <Paper elevation={6} sx={{ borderRadius: '16px', overflow: 'hidden', bgcolor: colors.blueGrey[50] }}>
        <AppBar position="static" sx={{ bgcolor: colors.blue[700], borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
          <Toolbar sx={{ justifyContent: 'center' }}>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
              Creative Writing Space
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          {/* Toolbar for formatting and creative options */}
          <Paper elevation={2} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5, mb: 2, borderRadius: '12px', bgcolor: colors.blueGrey[100] }}>
            <IconButton size="small" onClick={() => applyFormat('bold')}><FormatBoldIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('italic')}><FormatItalicIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('underline')}><FormatUnderlinedIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('strikeThrough')}><FormatStrikethroughIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('subscript')}><SubscriptIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('superscript')}><SuperscriptIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('insertUnorderedList')}><FormatListBulletedIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('insertOrderedList')}><FormatListNumberedIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('justifyLeft')}><FormatAlignLeftIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('justifyCenter')}><FormatAlignCenterIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('justifyRight')}><FormatAlignRightIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('justifyFull')}><FormatAlignJustifyIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('fontSize', '5')}><TextIncreaseIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('fontSize', '2')}><TextDecreaseIcon fontSize="small" /></IconButton>

            {/* Text Color Picker */}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <PaletteIcon sx={{ fontSize: 'small', color: colors.grey[700] }} />
              <input type="color" defaultValue="#000000" onChange={(e) => applyFormat('foreColor', e.target.value)} style={{ width: '24px', height: '24px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', ml: 0.5 }} />
            </Box>

            {/* Highlight Color Picker */}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <BorderColorIcon sx={{ fontSize: 'small', color: colors.grey[700] }} />
              <input type="color" defaultValue="#FFFF00" onChange={(e) => applyFormat('backColor', e.target.value)} style={{ width: '24px', height: '24px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', ml: 0.5 }} />
            </Box>

            {/* Font Family Selector */}
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120, ml: 1 }}>
              <InputLabel id="font-family-label">Font</InputLabel>
              <Select
                labelId="font-family-label"
                id="fontFamily"
                defaultValue="Inter"
                onChange={(e) => applyFormat('fontName', e.target.value)}
                label="Font"
              >
                <MenuItem value="Inter">Inter</MenuItem>
                <MenuItem value="Arial">Arial</MenuItem>
                <MenuItem value="Verdana">Verdana</MenuItem>
                <MenuItem value="Georgia">Georgia</MenuItem>
                <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                <MenuItem value="Courier New">Courier New</MenuItem>
                <MenuItem value="Comic Sans MS">Comic Sans MS</MenuItem>
                <MenuItem value="Pacifico">Pacifico</MenuItem>
                <MenuItem value="Handlee">Handlee</MenuItem>
              </Select>
            </FormControl>

            {/* New Creative Buttons */}
            <IconButton size="small" onClick={() => setShowDrawingCanvas(true)} sx={{ bgcolor: colors.purple[200], '&:hover': { bgcolor: colors.purple[300] } }}>
              <BrushIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setShowEmojiPicker(!showEmojiPicker)} sx={{ bgcolor: colors.amber[200], '&:hover': { bgcolor: colors.amber[300] }, position: 'relative' }}>
              <EmojiEmotionsIcon fontSize="small" />
              {showEmojiPicker && (
                <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, mt: 1, p: 1, borderRadius: '8px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '4px', zIndex: 1200 }}>
                  {emojis.map((emoji, index) => (
                    <Typography key={index} variant="body1" component="span" sx={{ cursor: 'pointer', '&:hover': { bgcolor: colors.grey[100], borderRadius: '4px' }, p: 0.5, fontSize: '1.2rem' }} onClick={() => handleInsertEmoji(emoji)}>
                      {emoji}
                    </Typography>
                  ))}
                </Paper>
              )}
            </IconButton>
            <IconButton size="small" onClick={insertPlaceholderImage} sx={{ bgcolor: colors.green[200], '&:hover': { bgcolor: colors.green[300] } }}>
              <ImageIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => applyFormat('undo')}><UndoIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('redo')}><RedoIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => applyFormat('removeFormat')} sx={{ bgcolor: colors.red[200], '&:hover': { bgcolor: colors.red[300] } }}>
              <ClearFormatIcon fontSize="small" />
            </IconButton>
          </Paper>

          {/* The editable writing area */}
          <Paper
            ref={editorRef}
            contentEditable={true}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            sx={{
              minHeight: '300px',
              height: 'auto',
              p: 3,
              border: `1px solid ${colors.grey[300]}`,
              borderRadius: '8px',
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
              '&:focus': { outline: 'none', borderColor: colors.blue[400], boxShadow: `0 0 0 2px ${colors.blue[200]}` },
              fontSize: '1.1rem',
              lineHeight: 1.6,
              color: colors.grey[800],
              overflowY: 'auto',
              // Custom scrollbar styles (Material UI doesn't have direct scrollbar props)
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: colors.grey[100], borderRadius: '10px' },
              '&::-webkit-scrollbar-thumb': { background: colors.blue[200], borderRadius: '10px' },
              '&::-webkit-scrollbar-thumb:hover': { background: colors.blue[300] },
            }}
            dangerouslySetInnerHTML={{ __html: content }} // Set initial content
          ></Paper>

          {/* Word and Character Count */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, fontSize: '0.85rem', color: colors.grey[600] }}>
            <Typography variant="caption" sx={{ bgcolor: colors.blue[50], color: colors.blue[800], px: 1.5, py: 0.5, borderRadius: '16px', fontWeight: 'medium' }}>Words: {wordCount}</Typography>
            <Typography variant="caption" sx={{ bgcolor: colors.deepPurple[50], color: colors.deepPurple[800], px: 1.5, py: 0.5, borderRadius: '16px', fontWeight: 'medium' }}>Characters: {charCount}</Typography>
          </Box>

          {/* Save/Load/Clear Buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={saveContent}>
              Save Progress
            </Button>
            <Button variant="contained" color="secondary" startIcon={<FolderOpenIcon />} onClick={loadContent}>
              Load Saved
            </Button>
            <Button variant="contained" color="error" startIcon={<DeleteForeverIcon />} onClick={clearContent}>
              Clear All
            </Button>
          </Box>

          {/* Message display for save/load actions */}
          {message && (
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: colors.blue[700], bgcolor: colors.blue[100], p: 1, borderRadius: '8px', animation: 'fade-in-out 3s forwards' }}>
              {message}
            </Typography>
          )}
        </Box>

        {/* Drawing Canvas Modal */}
        {showDrawingCanvas && (
          <DrawingCanvas
            onSave={handleSaveDrawing}
            onClose={() => setShowDrawingCanvas(false)}
          />
        )}
      </Paper>

      {/* Global styles for animations */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&family=Pacifico&family=Handlee&display=swap');
        body { margin: 0; font-family: 'Roboto', sans-serif; }
        @keyframes fade-in-out {
          0% { opacity: 0; transform: translateY(10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(10px); }
        }
      `}</style>
    </Container>
  );
};

export default WritingSpace;
